import { useEffect } from 'react'
import { ChevronRight, X, Zap } from '../../icons'
import type { Device, Sensor } from '../../types/device'
import { formatKW } from '../../lib/format'
import { PanelShell } from './PanelShell'

interface Props {
  devices: Device[]
  sensors: Sensor[]
  siteName: string
  showClose: boolean
  asSheet: boolean
  onClose: () => void
  onSelect: (id: string) => void
}

function isMeteredDevice(d: Device): boolean {
  return d.kind === 'meter-1ph' || d.kind === 'meter-3ph'
}

export function OverviewPanel({ devices, sensors, siteName, showClose, asSheet, onClose, onSelect }: Props) {
  useEffect(() => {
    if (!showClose) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose, showClose])

  // Active consumers: metered Shelly devices currently drawing power, plus
  // any Sintropy-sourced 'elettrico' sensor (e.g. shelly fotovoltaico) that's
  // online and reporting > 0 W.
  type Consumer = { id: string; name: string; powerW: number }
  const meteredDevices = devices.filter(isMeteredDevice)
  const deviceConsumers: Consumer[] = meteredDevices
    .filter((d) => d.online && (d.metrics?.power ?? 0) > 0)
    .map((d) => ({ id: d.id, name: d.name, powerW: d.metrics?.power ?? 0 }))
  const sensorConsumers: Consumer[] = sensors
    .filter((s) => s.kind === 'elettrico' && s.online && (s.readings.power ?? 0) > 0)
    .map((s) => ({ id: s.id, name: s.name, powerW: s.readings.power ?? 0 }))
  const allConsumers = [...deviceConsumers, ...sensorConsumers].sort((a, b) => b.powerW - a.powerW)
  const totalW = allConsumers.reduce((s, c) => s + c.powerW, 0)

  const onCount = devices.filter((d) => d.online && d.relay?.on).length
  const offCount = devices.filter((d) => d.online && d.relay && !d.relay.on).length
  const offlineCount = devices.filter((d) => !d.online).length
  const sensorOnlineCount = sensors.filter((s) => s.online).length
  const sensorOfflineCount = sensors.length - sensorOnlineCount

  const body = (
    <div className="dpanel dpanel--overview">
      <div className="dpanel__header">
        <div className="dpanel__title-wrap">
          <div className="dpanel__icon dpanel__icon--site">
            <Zap />
          </div>
          <div>
            <div className="dpanel__name">{siteName}</div>
            <div className="dpanel__id">Ristorante · {devices.length} dispositivi · {sensors.length} sensori</div>
          </div>
        </div>
        {showClose && (
          <button className="modal__close" onClick={onClose} aria-label="Chiudi">
            <X />
          </button>
        )}
      </div>

      <div className="ov-hero">
        <div className="ov-hero__label">Consumo attivo totale</div>
        <div className="ov-hero__value">
          <Zap />
          <span className="num">{formatKW(totalW)}</span>
          <span className="u">kW</span>
        </div>
        <div className="ov-hero__hint">Sommato dai quadri elettrici (QE + fotovoltaico)</div>
      </div>

      <div className="dpanel__section-label">Stato dispositivi</div>
      <div className="ov-stats">
        <div className="ov-stat ov-stat--on">
          <div className="ov-stat__k">Accesi</div>
          <div className="ov-stat__v">{onCount}</div>
        </div>
        <div className="ov-stat">
          <div className="ov-stat__k">Spenti</div>
          <div className="ov-stat__v">{offCount}</div>
        </div>
        <div className="ov-stat ov-stat--offline">
          <div className="ov-stat__k">Offline</div>
          <div className="ov-stat__v">{offlineCount}</div>
        </div>
        <div className="ov-stat">
          <div className="ov-stat__k">Quadri</div>
          <div className="ov-stat__v">{meteredDevices.length}</div>
        </div>
      </div>

      <div className="dpanel__section-label">Stato sensori</div>
      <div className="ov-stats">
        <div className="ov-stat ov-stat--on">
          <div className="ov-stat__k">Online</div>
          <div className="ov-stat__v">{sensorOnlineCount}</div>
        </div>
        <div className="ov-stat ov-stat--offline">
          <div className="ov-stat__k">Offline</div>
          <div className="ov-stat__v">{sensorOfflineCount}</div>
        </div>
      </div>

      <div className="dpanel__section-label dpanel__section-label--inline">
        <span>Dispositivi che consumano attivamente</span>
        <span className="count-pill">{allConsumers.length}</span>
      </div>
      {allConsumers.length === 0 ? (
        <div className="ov-empty">Nessun quadro sta consumando in questo momento.</div>
      ) : (
        <ul className="ov-consumers">
          {allConsumers.map((c) => (
            <li key={c.id}>
              <button className="ov-consumer" onClick={() => onSelect(c.id)}>
                <span className="ov-consumer__dot" />
                <span className="ov-consumer__name">{c.name}</span>
                <span className="ov-consumer__power">
                  {formatKW(c.powerW)} <span className="u">kW</span>
                </span>
                <span className="ov-consumer__chev"><ChevronRight /></span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="dpanel__section-label">Notifiche</div>
      <div className="ov-notif">
        <div className="ov-notif__title">Nessuna notifica</div>
        <div className="ov-notif__sub">Quando arriva qualcosa di nuovo, lo vedrai qui.</div>
      </div>

      <div className="dpanel__footer">
        <div className="dpanel__footer-k">Polling</div>
        <div className="dpanel__footer-v">Shelly 30s · Sensori 15min</div>
        <div className="dpanel__footer-k">Sito</div>
        <div className="dpanel__footer-v">OWW · Tradate (VA), IT</div>
      </div>
    </div>
  )

  return <PanelShell asSheet={asSheet} onClose={onClose} variant="overview">{body}</PanelShell>
}
